import { useMyEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import { useUploadImage } from 'lib-knowledge-management-common/hooks/useUploadImage';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import {
  ClipboardEvent as ReactClipboardEvent,
  DragEvent as ReactDragEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { imageUploadActions } from './imageUpload.store';
import {
  DropMode,
  findBlockAtPoint,
  getSelectedBlockId,
  getUploadableImageFiles,
  hasUploadableImageDrag,
  insertImageFiles,
  MAX_UPLOAD_FILES,
  resolveDropMode,
} from './imageUpload.utils';

export type UseImageFileDropReturn = {
  onDragOver: (dragEvent: ReactDragEvent<HTMLElement>) => void;
  onDragLeave: (dragEvent: ReactDragEvent<HTMLElement>) => void;
  onDrop: (dragEvent: ReactDragEvent<HTMLElement>) => void;
  onPaste: (clipboardEvent: ReactClipboardEvent<HTMLElement>) => void;
};

const DROP_ATTRIBUTE = 'imageDrop';

export function useImageFileDrop(): UseImageFileDropReturn {
  const editor = useMyEditorRef();
  const [uploadImage] = useUploadImage();
  const { errorNotify } = useNotification();
  const highlighted = useRef<HTMLElement | undefined>(undefined);

  const isWithinFileLimit = useCallback(
    (files: readonly File[]) => {
      if (files.length <= MAX_UPLOAD_FILES) return true;

      errorNotify({
        title: 'Too many images',
        text: `Up to ${MAX_UPLOAD_FILES} images at a time is allowed.`,
      });

      return false;
    },
    [errorNotify],
  );

  const clearHighlight = useCallback(() => {
    if (highlighted.current) delete highlighted.current.dataset[DROP_ATTRIBUTE];

    highlighted.current = undefined;
  }, []);

  const highlightDropTarget = useCallback(
    (element: HTMLElement, mode: DropMode) => {
      if (highlighted.current !== element) clearHighlight();

      element.dataset[DROP_ATTRIBUTE] = mode;
      highlighted.current = element;
    },
    [clearHighlight],
  );

  useEffect(() => {
    imageUploadActions.reset();

    return () => imageUploadActions.reset();
  }, []);

  const onDragOver = useCallback(
    (e: ReactDragEvent<HTMLElement>) => {
      if (!hasUploadableImageDrag(e.dataTransfer)) return;

      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';

      const target = findBlockAtPoint(editor, e.clientX, e.clientY);
      if (!target?.element) return;

      highlightDropTarget(target.element, resolveDropMode(editor, target.id));
    },
    [editor, highlightDropTarget],
  );

  const onDragLeave = useCallback(
    (e: ReactDragEvent<HTMLElement>) => {
      const movedTo = e.relatedTarget;
      if (movedTo instanceof Node && e.currentTarget.contains(movedTo)) {
        return;
      }

      clearHighlight();
    },
    [clearHighlight],
  );

  const onDrop = useCallback(
    (e: ReactDragEvent<HTMLElement>) => {
      clearHighlight();

      const files = getUploadableImageFiles(e.dataTransfer);
      if (!files.length) return;

      e.preventDefault();
      e.stopPropagation();

      if (!isWithinFileLimit(files)) return;

      insertImageFiles({
        blockId: findBlockAtPoint(editor, e.clientX, e.clientY)?.id,
        editor,
        files,
        uploadImage,
      });
    },
    [clearHighlight, editor, isWithinFileLimit, uploadImage],
  );

  const onPaste = useCallback(
    (e: ReactClipboardEvent<HTMLElement>) => {
      const files = getUploadableImageFiles(e.clipboardData);
      if (!files.length) return;

      e.preventDefault();
      e.stopPropagation();

      if (!isWithinFileLimit(files)) return;

      insertImageFiles({
        blockId: getSelectedBlockId(editor),
        editor,
        files,
        uploadImage,
      });
    },
    [editor, isWithinFileLimit, uploadImage],
  );

  return { onDragOver, onDragLeave, onDrop, onPaste };
}
